import {
  IPpPlanningWizardDetailsData,
  IPpPlanningWizardSelectionGridData,
  IPpWizardProvider,
  PpWizardStates
}                                                   from '../pp-planning-wizard.types';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {
  ApDynGridColumnConfigBase
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynformsConfigFieldset
}                                                   from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  filter,
  map,
  tap
}                                                   from 'rxjs/operators';
import {
  EventEmitter,
  Injectable
}                                                   from '@angular/core';
import {
  LanguageStore
}                                                   from '../../../../stores/translation/language.store';
import {
  SettingsStore
}                                                   from '../../../../stores/base-data/settings.store';
import {
  ApGuidUtil
}                                                   from '../../../../ap-utils';
import {
  ApDynGridPagerWizardBreadcrumbsStepper,
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  NotifyStore
}                                                   from '../../../../stores/dialog/notify.store';
import {
  IGridStatus
}                                                   from '../../../../ap-interface/interfaces/ap-grid-status.interface';
import {
  CropRotationStore
}                                                   from '../../../../stores/farm/crop.rotation.store';
import {
  FieldStore
}                                                   from '../../../../stores/farm/field.store';
import {
  CampaignYearStore
}                                                   from '../../../../stores/login/campaignyear.store';
import {
  CropTypeStore
}                                                   from '../../../../stores/base-data/crop.types.store';
import {
  PlantProtectionPlanBookStore
}                                                   from '../../../../stores/plant-protection/plant-protection-planbook.store';
import {
  ApGetCropService
}                                                   from '../../../../stores/services/ap-get-crop.service';
import {
  ApDynComponentComponent
}                                                   from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  SensorStore
}                                                   from '../../../../stores/sensor/sensor.store';
import {
  ScanService
}                                                   from '../../../../services/data/scan.service';
import {
  NdiStore
}                                                   from '../../../../stores/ndi/ndi.store';
import {
  PpPlanningWizardSelectionService
}                                                   from '../services/pp-planning-wizard-selection.service';
import {
  ObjectFactory
}                                                   from 'ts-tooling';
import {
  ApColumnState
}                                                   from '../../../../ap-interface/enums/ap-column-state.enum';
import {
  SelectionGridColumnsUiProvider
}                                                   from './ui-providers/selection.grid-columns.ui-provider';
import {
  FieldService
}                                                   from '../../../../field-management/components/field.service';
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;
import ICropTypes = Data.BaseData.ICropTypes;
import ISensorFieldData = Data.Sensor.ISensorFieldData;
import IFieldGeom = Data.FieldManagement.IFieldGeom;
import ICampaignYear = Data.Authentication.ICampaignYear;
import {CroptypeService}                            from '../../../../services/data/croptypes.service';
import {ApTranslationService}                       from '../../../../ap-utils/service/ap-translation.service';

// ATTENTION: injectable 'root' is very convenient in here because we can inject all needed stores.
// BUT: This will reuse THE SAME instance of the provider every time.
// Therefore, we need to carefully handle the 'onDestroy' method!
@Injectable({providedIn: 'root'})
export class PpPlanningWizardSelectionProvider implements IPpWizardProvider<IPpPlanningWizardSelectionGridData, IPpPlanningWizardSelectionGridData[]> {
  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;
  private _errorKeys: BehaviorSubject<string[]>;
  private _handleStateChange: EventEmitter<PpWizardStates>;
  private _isContinueActive: BehaviorSubject<boolean>;
  private _dynComponent: ApDynComponentComponent;

  constructor(private ndiStore: NdiStore,
              private fieldStore: FieldStore,
              private sensorStore: SensorStore,
              private notifyStore: NotifyStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private cropTypeStore: CropTypeStore,
              private cropRotationStore: CropRotationStore,
              private campaignYearStore: CampaignYearStore,
              private plantProtectionPlanBookStore: PlantProtectionPlanBookStore,
              private scanService: ScanService,
              private fieldService: FieldService,
              private cropService: ApGetCropService,
              private ppPlanningWizardSelectionService: PpPlanningWizardSelectionService,
              private selectionGridColumnsUiProvider: SelectionGridColumnsUiProvider,
              private croptypeService: CroptypeService,
              private translationService: ApTranslationService) {
  }

  /**
   * Initializes this wizard step's provider instance with necessary dependencies
   * and components required for the wizard functionality
   * @param caption$ Observable for the wizard step caption
   * @param fieldSets$ Observable for form field sets configuration
   * @param errorKeys Observable for tracking validation error keys
   * @param handleStateChange Event emitter to manage wizard state transitions
   * @param isContinueActive Observable controlling the continue button state
   * @param dynComponent Reference to the dynamic component instance
   * @public
   */
  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              errorKeys: BehaviorSubject<string[]>,
              handleStateChange: EventEmitter<PpWizardStates>,
              isContinueActive: BehaviorSubject<boolean>,
              dynComponent: ApDynComponentComponent): void {
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._errorKeys = errorKeys;
    this._handleStateChange = handleStateChange;
    this._isContinueActive = isContinueActive;
    this._dynComponent = dynComponent;
  }

  /**
   * Updates the caption for the selection wizard step
   * Sets the translation key for the step title
   * @public
   */
  public updateCaption(): void {
    this._caption$.next('Nutrients__SelectFieldsTitle');
  }

  /**
   * Whenever grid selection changes we might need to perform some validations...
   * @param items selected items
   * @public
   */
  public onGridSelectionChanged(items: IPpPlanningWizardSelectionGridData[]): void {
    this.fieldStore.changeSelectedField(items.map((item) => item?.Id?.toString()));
    const eppoCode = this.ppPlanningWizardSelectionService.getEppoCodeFromSelectedFields(items);
    const isValidSelection = items.length !== 0 && items.every(i => i?.Status.state >= ApColumnState.Yellow);
    const isValidCropsSelection = items?.length > 0 && items.every((i) => i?.EppoCode === eppoCode);
    this._isContinueActive.next(isValidSelection && isValidCropsSelection);
    if (!isValidCropsSelection && items.length > 0) {
      this._errorKeys.next(items.map(i => i.Id?.toString()));
      this.notifyStore.addMessage({
        Level: ApValidationLevel.Error,
        Counter: 1,
        ErrorKey: 'Global__Various_Crop_Selected',
        UserId: null,
        UserName: null,
        DisplayTimeout: 2,
        Parameters: null
      });
    } else {
      this._errorKeys.next([]);
    }
  }

  /**
   * Generates field statistics for the current grid selection
   * Determines which items to use for statistic generation based on the current grid state
   * @public
   */
  public onGenerateStatistic(): void {
    const totalItems = (this._dynComponent?.items as IPpPlanningWizardSelectionGridData[]) ?? [];
    let selectedItems = (this._dynComponent?.getSelectedItems() as IPpPlanningWizardSelectionGridData[]) ?? [];
    if (selectedItems.length === 0) {
      selectedItems = totalItems;
    }
    this.ppPlanningWizardSelectionService.generateFieldStatistic(selectedItems, totalItems);
  }

  /**
   * Builds dataItems for the selection wizard step
   * Combines data from multiple stores to create grid items with field, crop, and sensor information
   * Calculates status for each field based on crop compatibility and sensor data
   * @returns Observable of grid data items for the selection grid
   * @public
   */
  public buildDataItems(): Observable<IPpPlanningWizardSelectionGridData[]> {
    return combineLatest([
      this.fieldStore.Fields$,
      this.cropRotationStore.Listen(s => s.history),
      this.campaignYearStore.Listen(s => s.selectedYear),
      this.cropTypeStore.Listen(s => s.cropTypesNames),
      this.sensorStore.Listen(s => s.data),
      this.plantProtectionPlanBookStore.Listen(s => s.data),
      this.ndiStore.NdiStatistics$
    ]).pipe(
      filter(([fields, cropRotation]) => fields.length !== 0 && Object.keys(cropRotation).length !== 0),
      map(([fields, cropRotation, year, cropTypes, sensorFieldData, _, _1]) => fields.map((field) => {
        const geom = this.fieldStore.getCurrentFieldGeom(field);
        const fieldCrop = cropRotation[field.Id as string];
        const sensorData = sensorFieldData?.Find(sfd => sfd?.FieldGeomId === geom?.Id) as ISensorFieldData;
        let crop: any;
        let cropId: any;
        let defaultCropEppoCode: string | null;
        if (fieldCrop) {
          cropId = fieldCrop[year.Year].MainCrop;
          defaultCropEppoCode = this.cropTypeStore.getDefaultCroptype(cropId)?.Eppocode;
          crop = cropTypes[fieldCrop[year.Year].MainCrop];
        }
        const bestScanData = this.scanService.getBestScanData(field, this.getDefaultBestScanStatus());
        return {
          Id: field.Id,
          Status: this.getStatus(geom, year, sensorData, cropId),
          Nr: field.FieldNumber,
          TNr: field.FieldSubnumber,
          NrTNr: this.fieldStore.getFieldNumberAndSubNumber(field),
          Name: field.FieldName,
          Area: geom?.AreaHa ?? undefined,
          Crop: crop,
          CropId: cropId,
          EppoCode: defaultCropEppoCode,
          SecondCropId: (this.cropService.getCrop(field, false, 'object') as ICropTypes)?.Id,
          Geom: geom,
          PlanBookId: bestScanData.PlanId ?? ApGuidUtil.getEmptyGuid(),
          ScanData: bestScanData,
          CropCategory: this.translationService.translate(this.croptypeService.getCropCategoryForCurrentCampaignYear(field))
        } as IPpPlanningWizardSelectionGridData;
      })),
      tap((gridItems: IPpPlanningWizardSelectionGridData[]) => {
        const selectedItems = (this._dynComponent?.getSelectedItems() as IPpPlanningWizardSelectionGridData[]) ?? [];
        this.ppPlanningWizardSelectionService.generateFieldStatistic(selectedItems, gridItems);
      })
    );
  }

  /**
   * Creates the wizard selection (1st step) grid columns configuration
   * Builds a dynamic column configuration based on current language and system settings
   * @param _detailsData Unused details data parameter (maintained for interface compliance)
   * @returns Observable of a column configuration array for the selection grid
   * @public
   */
  public getGridColumnConfig(_detailsData: IPpPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.Listen(s => s.selectedLanguage),
      this.settingsStore.Listen(s => s.data)
    ]).pipe(
      filter(([language, settings]) => !!language && settings.length !== 0),
      map(([language, settings]) => ({language, setting: settings[0]})),
      map((p) => this.selectionGridColumnsUiProvider.generateGridColumns(p.setting, p.language))
    );
  }

  /**
   * Creates a pager configuration for the selection wizard step
   * Configures navigation buttons and breadcrumb stepper for the wizard
   * @param breadCrumbsColumns Array of wizard states for breadcrumb navigation
   * @returns Configured a pager object for the wizard
   * @public
   */
  public getPagerConfig(breadCrumbsColumns: PpWizardStates[]): ApDynGridPagerConfigBase {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global__Cancel', this._handleStateChange, {value: PpWizardStates.Cancel}),
      new ApDynGridPagerWizardBreadcrumbsStepper(breadCrumbsColumns, 0),
      new ApDynGridPagerWizardButton('Global__Next', this._handleStateChange,
        {active: this._isContinueActive, value: PpWizardStates.Details, primary: true}));
  }

  /**
   * Leaves the selection wizard step and updates selected fields
   * This method:
   * - Updates the selected fields in the field store
   * - Returns copy of the currently selected grid items
   * @returns Array of selected planning wizard selection grid data
   * @public
   */
  public onLeave(): IPpPlanningWizardSelectionGridData[] {
    this.fieldStore.changeSelectedField(this._dynComponent.dynGrid.gridPaging.selectedKeys?.map(k => k.toString()));
    const selectedFields = this._dynComponent.dynGrid.gridPaging.selectedItems as IPpPlanningWizardSelectionGridData[];
    return ObjectFactory.Copy(selectedFields);
  }

  /**
   * Clears field sets for the selection wizard step
   * Resets the field sets observable to an empty array
   * @public
   */
  public updateFieldSets(): void {
    this._fieldSets$.next([]);
  }

  /**
   * Cleanup method for the provider when destroyed
   * Currently empty, but can be used for additional cleanup if needed
   * @public
   */
  public onDestroy(): void {
    this.fieldStore.changeSelectedField([]);
  }

  /**
   * Determines the status of a field based on field geometry and crop data
   * Evaluates field status considering:
   * - Field geometry validation against campaign year
   * - Crop type existence and sensor crop compatibility
   * - Crop key matching between selected crop and sensor data
   * - Returns different status levels:
   *   - Red (-1): Invalid geometry, missing crop, or other error
   *   - Yellow (0): Non-sensor crop or mismatched crop types
   *   - Green (1): Valid sensor crop with matching data
   * @param fieldGeom Field geometry data
   * @param campaignYear Current campaign year settings
   * @param sensorData Associated sensor field data
   * @param mainCropId ID of the main crop for the field
   * @returns Grid status object with state, class, and description
   * @private
   */
  private getStatus(fieldGeom: IFieldGeom, campaignYear: ICampaignYear,
                    sensorData: ISensorFieldData, mainCropId: number | undefined): IGridStatus {
    const geomStatus = this.fieldService.getGeomStatus(fieldGeom, campaignYear);
    if (geomStatus === -1) {
      return {
        state: -1,
        class: 'ap-status-red',
        description: 'Global__Please_Contact_The_Support'
      };
    }
    const cropType = this.cropTypeStore.getDefaultCroptype(mainCropId);
    const result = {state: undefined, class: undefined, description: undefined} as IGridStatus;
    if (cropType && cropType.IsSensorCrop === true && (!sensorData || sensorData?.CropKey < 1 || cropType.CropKey === sensorData?.CropKey || cropType.Eppocode === sensorData?.EppoCode)) {
      result.state = 1;
      result.class = 'ap-status-green';
      result.description = 'Global__Variable_Planning_Possible';
    } else if (!cropType || cropType?.Id < 0) {
      result.state = -1;
      result.class = 'ap-status-red';
      result.description = 'Global__CropGroup_Nothing';
    } else if (cropType && !cropType?.IsSensorCrop) {
      result.state = 0;
      result.class = 'ap-status-yellow';
      result.description = 'Global__No_Var_Planning_SensorCrop';
    } else if (cropType && sensorData && cropType.Id !== sensorData.CroptypeId) {
      result.state = 1;
      result.class = 'ap-status-yellow';
      result.description = 'Global__Crop_not_equals_SensorCrop';
    } else {
      result.state = -1;
      result.class = 'ap-status-red';
      result.description = '';
    }
    return result;
  }

  /**
   * Provides a default grid status for best scan
   * @returns Default grid status with gray state and empty description
   * @private
   */
  private getDefaultBestScanStatus(): IGridStatus {
    return {
      state: ApColumnState.Red,
      class: 'ap-status-grey',
      description: ''
    };
  }
}
